/**
 * HR Dashboard Page
 * Main dashboard for viewing HR summary and company details
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Percent, 
  Calendar,
  Building2,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useSupabaseCompanies } from '@/hooks/useSupabaseCompanies';
import { getHRData, transformHRData, getHRChartData, getDepartmentName } from '@/integrations/sqlserver/hrService';
import { HRSummary, HRFilters } from '@/types/hr';
import { formatNumberDisplay } from '@/lib/utils';
import CompanySummaryTable from '@/components/CompanySummaryTable';

const HRDashboard: React.FC = () => {
  // Get today's date as default
  const today = new Date().toISOString().split('T')[0];
  
  // States
  const [filters, setFilters] = useState<HRFilters>({
    fromDate: today,
    toDate: today,
    compIDs: [1, 2, 3, 20] // Default companies (exclude legacy 23, 24)
  });
  
  const [hrSummary, setHRSummary] = useState<HRSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  
  // Get companies from Supabase
  const { data: companies, isLoading: companiesLoading } = useSupabaseCompanies();
  
  // Filter companies to exclude legacy IDs 23 and 24
  const availableCompanies = useMemo(() => {
    return companies?.filter(company => 
      company.legacy_id !== 23 && 
      company.legacy_id !== 24
    ) || [];
  }, [companies]);

  // Fetch HR data
  const fetchHRData = async () => {
    try {
      setError(null);
      const rawData = await getHRData(filters);
      const transformedData = transformHRData(rawData);
      setHRSummary(transformedData);
      console.log('👥 HR Dashboard: Data loaded successfully:', transformedData);
    } catch (err: any) {
      console.error('👥 HR Dashboard: Error fetching data:', err);
      setError(err.message || 'Không thể tải dữ liệu nhân sự');
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  // Auto-fetch data when filters change
  useEffect(() => {
    if (filters.compIDs.length > 0) {
      fetchHRData();
    }
  }, [filters]);

  // Handle filter changes - auto-sync both dates
  const handleDateChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      fromDate: value,
      toDate: value // Auto-sync both dates to same value
    }));
  };

  // Navigate to previous day
  const handlePreviousDay = () => {
    const currentDate = new Date(filters.fromDate);
    currentDate.setDate(currentDate.getDate() - 1);
    const newDate = currentDate.toISOString().split('T')[0];
    handleDateChange(newDate);
  };

  // Navigate to next day
  const handleNextDay = () => {
    const currentDate = new Date(filters.fromDate);
    currentDate.setDate(currentDate.getDate() + 1);
    const newDate = currentDate.toISOString().split('T')[0];
    handleDateChange(newDate);
  };

  const handleRefresh = () => {
    setIsRefetching(true);
    fetchHRData();
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${formatNumberDisplay(value, 1)}%`;
  };

  // Get attendance rate color
  const getAttendanceRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600 bg-green-50';
    if (rate >= 85) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Loading state
  if (isLoading || companiesLoading) {
    return (
      <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Nhân sự</h1>
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Users className="h-8 w-8 text-blue-600" />
        <h1 className="text-3xl font-bold text-gray-900">Nhân sự</h1>
      </div>

      {/* Filters - Responsive with Refresh Button */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            {/* Time label */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Thời gian:</span>
            </div>
            
            {/* Single date picker with navigation - responsive and mobile-optimized */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-4 flex-grow">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                <Label htmlFor="selectedDate" className="text-sm font-medium text-gray-700">
                  Ngày báo cáo
                </Label>
                <div className="flex items-center gap-2">
                  {/* Previous day button */}
                  <Button
                    onClick={handlePreviousDay}
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 w-10 sm:w-9 p-0 rounded-lg border-gray-300 hover:bg-blue-50 hover:border-blue-400"
                    disabled={isRefetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {/* Date picker */}
                  <Input
                    id="selectedDate"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="w-full sm:w-44 h-10 sm:h-9 text-sm rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  
                  {/* Next day button */}
                  <Button
                    onClick={handleNextDay}
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 w-10 sm:w-9 p-0 rounded-lg border-gray-300 hover:bg-blue-50 hover:border-blue-400"
                    disabled={isRefetching}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Refresh button - mobile-optimized */}
            <Button 
              onClick={handleRefresh} 
              disabled={isRefetching}
              variant="outline"
              className="flex items-center justify-center gap-2 flex-shrink-0 w-full sm:w-auto h-10 sm:h-9 rounded-lg border-gray-300 hover:bg-blue-50 hover:border-blue-400 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Làm mới</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Global Loading Overlay */}
      {isRefetching && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-lg shadow-lg border">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-base font-medium text-gray-700">Đang tải dữ liệu nhân sự...</span>
          </div>
        </div>
      )}

      {/* Company Summary Table */}
      {hrSummary && (
        <CompanySummaryTable hrSummary={hrSummary} />
      )}

      {/* Company Tabs - Chi tiết các bộ phận */}
      {hrSummary && hrSummary.companies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Chi tiết theo bộ phận
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={hrSummary.companies[0]?.compID.toString()} className="w-full">
              <TabsList className="flex flex-wrap w-full gap-2 h-auto p-2">
                {hrSummary.companies.map((company) => (
                  <TabsTrigger 
                    key={company.compID} 
                    value={company.compID.toString()}
                    className="flex flex-col items-center gap-1 p-3 h-auto flex-1 min-w-[120px] sm:min-w-[140px]"
                  >
                    <span className="font-medium">{company.compName}</span>
                    <Badge 
                      variant="secondary" 
                      className={getAttendanceRateColor(company.attendanceRate)}
                    >
                      {formatPercentage(company.attendanceRate)}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>

              {hrSummary.companies.map((company) => (
                <TabsContent key={company.compID} value={company.compID.toString()} className="mt-6">
                  {/* Company Summary - 2x2 grid on mobile, 4 cols on desktop */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                    <Card>
                      <CardContent className="p-3 md:p-4">
                        <div className="text-center">
                          <p className="text-xs md:text-sm text-gray-600">Sĩ số</p>
                          <p className="text-lg md:text-xl font-bold text-blue-600">
                            {formatNumberDisplay(company.totalStaff, 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 md:p-4">
                        <div className="text-center">
                          <p className="text-xs md:text-sm text-gray-600">Vắng</p>
                          <p className="text-lg md:text-xl font-bold text-red-600">
                            {formatNumberDisplay(company.absent, 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 md:p-4">
                        <div className="text-center">
                          <p className="text-xs md:text-sm text-gray-600">Đi làm</p>
                          <p className="text-lg md:text-xl font-bold text-green-600">
                            {formatNumberDisplay(company.present, 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 md:p-4">
                        <div className="text-center">
                          <p className="text-xs md:text-sm text-gray-600">Tỷ lệ</p>
                          <p className={`text-lg md:text-xl font-bold ${getAttendanceRateColor(company.attendanceRate).split(' ')[0]}`}>
                            {formatPercentage(company.attendanceRate)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Department Details Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg">Chi tiết theo bộ phận - {company.compName}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6">
                      <div className="overflow-x-auto -mx-3 sm:mx-0">
                        <table className="w-full border-collapse border border-gray-200">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="border border-gray-200 px-2 sm:px-4 py-2 text-left font-semibold text-xs sm:text-sm">Bộ phận</th>
                              <th className="border border-gray-200 px-1 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm min-w-[50px] sm:min-w-[70px]">Sĩ số</th>
                              <th className="border border-gray-200 px-1 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm min-w-[55px] sm:min-w-[75px]">Đi làm</th>
                              <th className="border border-gray-200 px-1 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm min-w-[45px] sm:min-w-[65px]">Vắng</th>
                              <th className="border border-gray-200 px-1 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm min-w-[60px] sm:min-w-[80px]">Tỷ lệ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {company.departments
                              .sort((a, b) => b.present - a.present) // Sort by attendance count (high to low)
                              .map((dept, index) => (
                              <tr key={dept.deptCode} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border border-gray-200 px-2 sm:px-4 py-2 font-medium">
                                  <div>
                                    <div className="font-semibold text-xs sm:text-sm">{getDepartmentName(dept.deptCode)}</div>
                                    <div className="text-xs text-gray-500">{dept.deptCode}</div>
                                  </div>
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-4 py-2 text-center text-xs sm:text-sm">
                                  {formatNumberDisplay(dept.totalStaff, 0)}
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-4 py-2 text-center text-green-600 font-semibold text-xs sm:text-sm">
                                  {formatNumberDisplay(dept.present, 0)}
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-4 py-2 text-center text-red-600 font-semibold text-xs sm:text-sm">
                                  {formatNumberDisplay(dept.absent, 0)}
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-4 py-2 text-center">
                                  <Badge 
                                    variant="secondary" 
                                    className={`${getAttendanceRateColor(dept.attendanceRate)} text-xs px-1 sm:px-2 py-1`}
                                  >
                                    {formatPercentage(dept.attendanceRate)}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HRDashboard;